import { PrimaryCta, SubsectionGroup } from '@sleepstation/components'
import { LAYOUT_SPACING } from '@sleepstation/const'
import { themeFontBody } from '@sleepstation/style-api-helpers'
import { classStartsWith } from '@sleepstation/style-mixins'
import { defaultDarkerTheme } from '@sleepstation/themes-public-site'
import PropTypes from 'prop-types'
import { equals, isEmpty } from 'ramda'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import RESULT_VARIATIONS from '../../../../../const/resultVariations'
import { edgesNodesToList } from '../../../../../utils/graphql'
import BreakoutModuleNarrow from '../../../../shared/breakoutModules/BreakoutModuleNarrow'
import BreakoutModuleWide from '../../../../shared/breakoutModules/BreakoutModuleWide'
import StarscapeCallToActionBreakoutModule from '../../../../shared/breakoutModules/StarscapeCallToActionBreakoutModule'
import ConsiderationList from './ConsiderationList'
import SecondaryCtas from './SecondaryCtas'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const CTA_TRACKING_ID = `sleep-review-results-cta`

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled.div.attrs({ 'data-cy': `results-section` })``

const Result = styled.div.attrs({ 'data-cy': `results-section-result` })`
  ${classStartsWith(`SubsectionTitle`)} {
    ${themeFontBody()}
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const ResultSection = ({ data }) => {
  const { considerations, result, enrolment, primaryCta, secondaryCtas } = data

  const { title, action, actionText, variation, forceAccepted } = result

  const { title: primaryCtaTitle } = primaryCta

  const { canAccessTherapy } = enrolment

  const flattenedConsiderations = edgesNodesToList(considerations)

  const padding = isEmpty(flattenedConsiderations)
    ? [
        [2, 1],
        [2, 1],
        [2.5, 2],
      ]
    : [2, 2, [2.5, 4]]

  const shouldShowCta =
    !equals(variation, RESULT_VARIATIONS.HARD_REJECT) || forceAccepted

  return (
    <Layout>
      <BreakoutModuleNarrow
        hasGradient
        hasBottomBorder={false}
        paddingV={padding}
      >
        <Result>
          <SubsectionGroup title={title} spacing={LAYOUT_SPACING.X_LARGE}>
            <ConsiderationList considerations={flattenedConsiderations} />
          </SubsectionGroup>
        </Result>
      </BreakoutModuleNarrow>
      {shouldShowCta && (
        <ThemeProvider theme={defaultDarkerTheme}>
          <StarscapeCallToActionBreakoutModule topColor="backgroundEnd">
            <PrimaryCta
              title={primaryCtaTitle}
              linkLabel={actionText}
              linkUrl={action}
              trackingId={CTA_TRACKING_ID}
              forceOpenInSameWindow
            />
          </StarscapeCallToActionBreakoutModule>
        </ThemeProvider>
      )}
      <BreakoutModuleWide
        hasGradient
        hasTopBorder
        paddingV={[
          [2, 1],
          [2, 2],
          [2, 3],
        ]}
      >
        <SecondaryCtas
          secondaryCtas={secondaryCtas}
          showDownload={!canAccessTherapy}
        />
      </BreakoutModuleWide>
    </Layout>
  )
}

ResultSection.propTypes = {
  data: PropTypes.object.isRequired,
}

ResultSection.defaultProps = {}

export default ResultSection
